import { useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ScoreInput } from './ScoreInput';
import { BetPlaceholder } from './BetPlaceholder';
import type { HoleInfo, HoleScore, Team } from '@/lib/golfEngine';
import { cn } from '@/lib/utils';

interface SnapCarouselProps {
  holes: HoleInfo[];
  teams: Team[];
  scores: HoleScore[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onScoreChange: (playerId: string, holeNumber: number, score: number | null) => void;
}

function HolePanel({ hole, teams, scores, isActive, onScoreChange, onPrev, onNext, hasPrev, hasNext }: {
  hole: HoleInfo;
  teams: Team[];
  scores: HoleScore[];
  isActive: boolean;
  onScoreChange: (playerId: string, holeNumber: number, score: number | null) => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}) {
  const getScore = (playerId: string, holeNumber: number) =>
    scores.find((s) => s.playerId === playerId && s.holeNumber === holeNumber);

  if (!isActive) {
    return (
      <div className="bg-gray-50/80 rounded-lg border p-2 opacity-50 pointer-events-none h-full" data-testid={`side-panel-${hole.number}`}>
        <div className="text-center mb-2">
          <div className="text-sm font-bold text-muted-foreground">Hole {hole.number}</div>
          <div className="text-[10px] text-muted-foreground">Par {hole.par}</div>
        </div>
        <div className="space-y-1">
          {teams.flatMap((team) =>
            team.players.map((player) => {
              const sc = getScore(player.id, hole.number);
              return (
                <div key={player.id} className="flex justify-between items-center bg-white rounded px-2 py-1 text-xs border">
                  <span className="truncate text-[10px]">{player.name}</span>
                  <span className="font-bold ml-1">{sc?.grossScore ?? '-'}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full" data-testid={`active-panel-${hole.number}`}>
      <div className="flex items-center justify-between mb-2">
        <Button
          variant="outline"
          className="min-w-[44px] min-h-[44px]"
          onClick={onPrev}
          disabled={!hasPrev}
          data-testid="btn-prev-hole"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <div className="text-lg font-bold">Hole {hole.number}</div>
          <div className="text-xs text-muted-foreground">
            Par {hole.par} &middot; {hole.yardage} yds &middot; SI {hole.strokeIndex}
          </div>
        </div>
        <Button
          variant="outline"
          className="min-w-[44px] min-h-[44px]"
          onClick={onNext}
          disabled={!hasNext}
          data-testid="btn-next-hole"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      <div className="space-y-2">
        {teams.map((team, teamIdx) => (
          <div key={team.id} className="space-y-1.5">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Team {teamIdx + 1}
            </div>
            {team.players.map((player) => {
              const sc = getScore(player.id, hole.number);
              return (
                <ScoreInput
                  key={player.id}
                  playerName={player.name}
                  score={sc?.grossScore ?? null}
                  par={hole.par}
                  receivesStroke={sc?.receivesStroke ?? false}
                  onChange={(val) => onScoreChange(player.id, hole.number, val)}
                  teamColor={teamIdx === 0 ? 'team1' : 'team2'}
                />
              );
            })}
          </div>
        ))}
      </div>
      <BetPlaceholder />
    </div>
  );
}

export function SnapCarousel({ holes, teams, scores, currentIndex, onIndexChange, onScoreChange }: SnapCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);

  const scrollToIndex = useCallback((idx: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const panels = container.children;
    if (panels[idx]) {
      isScrolling.current = true;
      (panels[idx] as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      setTimeout(() => { isScrolling.current = false; }, 400);
    }
  }, []);

  useEffect(() => {
    scrollToIndex(currentIndex);
  }, [currentIndex, scrollToIndex]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let timeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      if (isScrolling.current) return;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const panels = Array.from(container.children) as HTMLElement[];
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        let closestIdx = 0;
        let closestDist = Infinity;
        panels.forEach((panel, i) => {
          const rect = panel.getBoundingClientRect();
          const panelCenter = rect.left + rect.width / 2;
          const dist = Math.abs(panelCenter - containerCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        });
        if (closestIdx !== currentIndex) {
          onIndexChange(closestIdx);
        }
      }, 100);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
    };
  }, [currentIndex, onIndexChange]);

  const prevHoleIdx = currentIndex > 0 ? currentIndex - 1 : null;

  const visibleIndices = [prevHoleIdx, currentIndex].filter((i): i is number => i !== null);

  return (
    <div data-testid="snap-carousel">
      <div className="flex items-center justify-center px-3 py-1">
        <div className="flex gap-1">
          {holes.map((h, i) => (
            <button
              key={h.number}
              onClick={() => onIndexChange(i)}
              className={cn(
                'w-2 h-2 rounded-full transition-all',
                i === currentIndex ? 'bg-primary w-4' : 'bg-gray-300'
              )}
              data-testid={`dot-${h.number}`}
            />
          ))}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-2 px-2 pb-4 scroll-smooth"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {visibleIndices.map((idx) => {
          const hole = holes[idx];
          const isActive = idx === currentIndex;
          return (
            <div
              key={hole.number}
              className={cn(
                'snap-center flex-shrink-0 rounded-xl p-3 border transition-all',
                isActive ? 'w-[75%] bg-white shadow-md border-primary/30' : 'w-[20%] bg-gray-50'
              )}
            >
              <HolePanel
                hole={hole}
                teams={teams}
                scores={scores}
                isActive={isActive}
                onScoreChange={onScoreChange}
                onPrev={() => onIndexChange(Math.max(0, currentIndex - 1))}
                onNext={() => onIndexChange(Math.min(holes.length - 1, currentIndex + 1))}
                hasPrev={currentIndex > 0}
                hasNext={currentIndex < holes.length - 1}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
